/**
 * A Client's response settings (Story 19, design §9.7): whether the
 * response layer may act on its endpoints and users, test or live, the
 * daily cap and the blast-radius bail-out. Off by default; enabling starts
 * in test mode, and the form says so before the admin turns live on.
 */

import { useState } from 'react';

import type { ClientTenant, ResponseMode, ResponseSettingsUpdate } from '@bluesphere/api-client';
import { PillButton } from '@bluesphere/ui-kit';

import { ErrorNote, INPUT, ModalShell } from '@bluesphere/mdr-alert-panel';

export function ResponseSettingsModal({
  tenant,
  userActionsAvailable,
  onCancel,
  onSubmit,
  isPending,
  error,
}: {
  tenant: ClientTenant;
  /** False until an identity connector is registered: the switch shows, disabled. */
  userActionsAvailable: boolean;
  onCancel: () => void;
  onSubmit: (body: ResponseSettingsUpdate) => void;
  isPending: boolean;
  error: unknown;
}) {
  const current = tenant.response;
  const [endpoint, setEndpoint] = useState(current.endpoint_enabled);
  const [user, setUser] = useState(current.user_enabled);
  const [mode, setMode] = useState<ResponseMode>(current.mode);
  const [cap, setCap] = useState(String(current.daily_cap));
  const [radius, setRadius] = useState(String(current.blast_radius));

  const capValue = Number(cap);
  const radiusValue = Number(radius);
  const numbersOk =
    Number.isInteger(capValue) && capValue >= 0 && capValue <= 1000 &&
    Number.isInteger(radiusValue) && radiusValue >= 1 && radiusValue <= 10000;
  const wasEnabled = current.endpoint_enabled || current.user_enabled;
  const nowEnabled = endpoint || user;
  const goingLive = nowEnabled && mode === 'live' && (current.mode !== 'live' || !wasEnabled);
  const canSubmit = numbersOk && !isPending;

  return (
    <ModalShell onClose={onCancel} width="max-w-lg">
      <div className="border-b border-bluesphere-border px-6 py-5">
        <h2 className="text-lg font-semibold text-bluesphere-textOnLight">
          Response settings for {tenant.display_name}
        </h2>
        <p className="mt-1 text-sm text-bluesphere-textSecondary">
          Whether BlueSphere may contain threats on this Client: cut endpoints off the network, lock down users,
          and release them. Every request still passes the safety rails; these switches are the
          Client's agreement. Off means the buttons on this Client's alerts explain why they are off.
        </p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) {
            onSubmit({
              endpoint_enabled: endpoint,
              user_enabled: user,
              mode,
              daily_cap: capValue,
              blast_radius: radiusValue,
            });
          }
        }}
        className="space-y-5 px-6 py-5"
      >
        <fieldset className="space-y-3">
          <legend className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
            Enabled for
          </legend>
          <Switch
            checked={endpoint}
            onChange={setEndpoint}
            label="Endpoints"
            hint="Isolate and release devices through Defender for Endpoint and SentinelOne."
          />
          <Switch
            checked={user}
            onChange={setUser}
            disabled={!userActionsAvailable}
            label="Users"
            hint={
              userActionsAvailable
                ? 'Disable a compromised account and revoke its sessions in Microsoft Entra ID; re-enable it afterwards.'
                : 'Arrives with the Entra identity connector; until then no user action is offered.'
            }
          />
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
            Mode
          </legend>
          <Radio
            checked={mode === 'test'}
            onChange={() => setMode('test')}
            label="Test"
            hint="Every request is evaluated and recorded as what would have happened. Nothing reaches a provider."
          />
          <Radio
            checked={mode === 'live'}
            onChange={() => setMode('live')}
            label="Live"
            hint="Requests that pass the rails execute against the Client's environment."
          />
          {!wasEnabled && nowEnabled && mode === 'test' && (
            <p className="text-xs text-bluesphere-muted">
              A newly enabled Client starts in test mode. Turn live on once the test records look
              right.
            </p>
          )}
          {goingLive && (
            <p className="rounded-input border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Live mode: the next request an operator confirms will act on{' '}
              <strong>{tenant.display_name}</strong>'s environment.
            </p>
          )}
        </fieldset>

        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
              Daily cap
            </span>
            <input
              type="number"
              min={0}
              max={1000}
              value={cap}
              onChange={(e) => setCap(e.target.value)}
              className={INPUT}
            />
            <span className="mt-1 block text-xs text-bluesphere-muted">
              Live actions per day. Past it, an operator must acknowledge; a rule is stopped.
            </span>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
              Blast radius
            </span>
            <input
              type="number"
              min={1}
              max={10000}
              value={radius}
              onChange={(e) => setRadius(e.target.value)}
              className={INPUT}
            />
            <span className="mt-1 block text-xs text-bluesphere-muted">
              Assets on one alert above which containment needs an acknowledgment.
            </span>
          </label>
        </div>

        <ErrorNote error={error} />
        <div className="flex items-center justify-end gap-2 pt-1">
          <PillButton variant="ghost" size="sm" type="button" onClick={onCancel}>
            Cancel
          </PillButton>
          <PillButton size="sm" type="submit" disabled={!canSubmit}>
            {isPending ? 'Saving…' : 'Save settings'}
          </PillButton>
        </div>
      </form>
    </ModalShell>
  );
}

/** A labeled on/off switch: a checkbox styled as a pill, keyboard and screen-reader native. */
export function Switch({
  checked,
  onChange,
  label,
  hint,
  disabled = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-start gap-3 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
    >
      <span className="relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center">
        <input
          type="checkbox"
          role="switch"
          aria-checked={checked}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="peer sr-only"
        />
        <span className="absolute inset-0 rounded-full bg-bluesphere-borderMuted transition peer-checked:bg-bluesphere-secondary peer-focus-visible:ring-2 peer-focus-visible:ring-bluesphere-secondary/50" />
        <span className="absolute left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition peer-checked:translate-x-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-bluesphere-textOnLight">{label}</span>
        {hint && <span className="block text-xs text-bluesphere-muted">{hint}</span>}
      </span>
    </label>
  );
}

function Radio({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  hint: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="radio"
        name="response-mode"
        checked={checked}
        onChange={onChange}
        className="mt-1 h-4 w-4 border-bluesphere-borderMuted text-bluesphere-secondary"
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-bluesphere-textOnLight">{label}</span>
        <span className="block text-xs text-bluesphere-muted">{hint}</span>
      </span>
    </label>
  );
}

/** One line for a table cell: "off", or "endpoints · test · 20/day". */
export function responseSummary(tenant: ClientTenant): string {
  const s = tenant.response;
  const scopes = [s.endpoint_enabled && 'endpoints', s.user_enabled && 'users'].filter(Boolean);
  if (scopes.length === 0) return 'off';
  return `${scopes.join(' + ')} · ${s.mode} · ${s.daily_cap}/day`;
}
